import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CreditcardIcon, MobilePhoneIcon, WalletPlusIcon } from '../../../../icons';
import { TooltipComponent } from '../../../atomic/feedback';
import { CardKind, CardStatusCard } from '../../../../../queries/pxtraCard';

// The virtual card lives on the phone, the physical one is the plastic. The
// kind is only ever carried by the icon, so the two must not look alike.
const kindIcons: Record<CardKind, typeof CreditcardIcon> = {
  virtual: MobilePhoneIcon,
  physical: CreditcardIcon
};

/**
 * One line per card the person holds, so the column stays one column however
 * many they have. Every icon carries a tooltip: an icon nobody can name is
 * decoration.
 */
export const CardsCell: FC<{ cards: CardStatusCard[] }> = ({ cards }) => {
  const { t } = useTranslation();

  if (cards.length === 0) {
    return (
      <Typography variant="body2" color="grey.500">
        –
      </Typography>
    );
  }

  return (
    <Box className="flex flex-col gap-y-1">
      {cards.map((card) => {
        const KindIcon = kindIcons[card.kind] ?? CreditcardIcon;
        const wallets = card.wallet_providers
          .map((provider) => t(`pxtraCard:wallet_${provider}`, { defaultValue: provider }))
          .join(', ');

        return (
          <Box
            key={`${card.kind}-${card.last_four_digits}`}
            className="flex items-center gap-x-2 whitespace-nowrap"
          >
            <TooltipComponent title={t(`pxtraCard:kind_${card.kind}`)} placement="top" arrow>
              <span className="flex items-center">
                <KindIcon width={18} height={18} color="grey.600" />
              </span>
            </TooltipComponent>
            <Typography variant="body2" className="leading-4">
              •••• {card.last_four_digits || '––––'}
            </Typography>
            {wallets && (
              <TooltipComponent title={t('pxtraCard:inWallet', { wallets })} placement="top" arrow>
                <span className="flex items-center">
                  <WalletPlusIcon width={16} height={16} color="grey.500" />
                </span>
              </TooltipComponent>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
